/**
 * Product tabs.
 *
 * Desktop: click a tab button to show the matching panel and hide the others.
 *   Tab/panel pairing is via `[data-tab-target]` → `[id]`.
 *
 * Mobile (<768px): each panel renders its own inline accordion trigger.
 *   The triggers toggle their panel's `data-accordion-open` attribute;
 *   CSS shows/hides the panel body based on that. Desktop tab activation
 *   syncs `data-accordion-open` so resizing keeps a sensible state.
 */
import { Component } from "@theme/component";

class ProductTabs extends Component {
  setup() {
    this.tabs = this.$$("[data-tab-target]");
    this.accordionTriggers = this.$$("[data-accordion-trigger]");

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.#activate(tab));
    });

    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => this.#toggleAccordion(trigger));
    });
  }

  #activate(tab) {
    const targetId = tab.dataset.tabTarget;

    this.tabs.forEach((t) => {
      const isActive = t === tab;
      t.setAttribute("aria-selected", String(isActive));
      t.classList.toggle("is-active", isActive);
    });

    this.$$("[data-tab-panel]").forEach((panel) => {
      const isActive = panel.id === targetId;
      panel.toggleAttribute("hidden", !isActive);
      panel.dataset.accordionOpen = String(isActive);
      const trigger = panel.querySelector("[data-accordion-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", String(isActive));
    });
  }

  #toggleAccordion(trigger) {
    const panel = trigger.closest("[data-tab-panel]");
    if (!panel) return;
    const isOpen = panel.dataset.accordionOpen === "true";
    panel.dataset.accordionOpen = String(!isOpen);
    trigger.setAttribute("aria-expanded", String(!isOpen));
  }
}

customElements.define("product-tabs", ProductTabs);

export { ProductTabs };
